import { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { RefreshCw } from "lucide-react";
import { useIsCentraleGT } from "@/hooks/useIsCentraleGT";
import { useIsHqSupportProfile } from "@/hooks/useIsHqSupportProfile";
import { useGTNetworkDepartments } from "@/hooks/useGTNetworkDepartments";
import { dashboardForRole } from "@/hooks/useStartUserPreview";
import { usePreviewAwareAuth } from "@/contexts/SuperAdminPreviewContext";

interface RouteGuardProps {
  children: React.ReactNode;
  allowedRoles: string[];
  redirectTo?: string;
}

type GuardStatus = "loading" | "authorized" | "no_profile" | "unauthorized" | "logged_out";

export function RouteGuard({ children, allowedRoles, redirectTo = "/dashboard" }: RouteGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, userRole, isLoading, isAuthenticated, isLoggingOut, isPreviewing, preview, realUserId } = usePreviewAwareAuth();
  const { isCentraleGT, isLoading: isGTLoading } = useIsCentraleGT();
  const { isHqSupport, isLoading: isHqSupportLoading } = useIsHqSupportProfile();
  const { departmentIds: gtMappedDeptIds, isLoading: isGTDeptLoading } = useGTNetworkDepartments(
    userRole === "chef_rayon" && isCentraleGT && !isHqSupport ? userProfile?.department_id : undefined
  );
  const hasRealGTMapping = (gtMappedDeptIds?.length ?? 0) > 0;
  const hasRedirected = useRef(false);
  const [guardStatus, setGuardStatus] = useState<GuardStatus>("loading");

  const handleRedirect = useCallback((path: string) => {
    if (!hasRedirected.current) {
      hasRedirected.current = true;
      navigate(path, { replace: true });
    }
  }, [navigate]);

  // Reset redirect flag when route changes
  useEffect(() => {
    hasRedirected.current = false;
    setGuardStatus("loading");
  }, [location.pathname]);

  useEffect(() => {
    // Logout in progress: do NOT redirect — signOut() owns the navigation.
    if (isLoggingOut) {
      setGuardStatus("logged_out");
      return;
    }

    // Wait for auth and GT detection to finish loading
    if (isLoading || (userRole === "chef_rayon" && isGTLoading) || isHqSupportLoading || isGTDeptLoading) {
      setGuardStatus("loading");
      return;
    }

    // Not authenticated → silent redirect to /auth
    if (!isAuthenticated || !user) {
      console.warn("[RouteGuard] No active session, redirecting to /auth");
      setGuardStatus("logged_out");
      handleRedirect("/auth");
      return;
    }

    // Session OK but role not resolved (profile missing or RPC failed)
    if (!userRole) {
      console.warn("[RouteGuard] Session active but no role resolved for user:", user.id);
      setGuardStatus("no_profile");
      return; // Show inline error page, no toast, no redirect
    }

    // HQ support profiles (Accueil/Sécurité) always use the operational HQ dashboard.
    if (
      isHqSupport &&
      (location.pathname === "/centrale/rdv" ||
        location.pathname === "/centrale/dashboard" ||
        location.pathname === "/sector-dashboard")
    ) {
      handleRedirect("/dashboard");
      return;
    }

    // Legacy supplier_guest accounts → /qr-dashboard (vue simplifiée).
    // NB: les fournisseurs autonomes (role='fournisseur', supplier_account_type='independent',
    // créés via QR) accèdent désormais au dashboard fournisseur complet.
    if (
      location.pathname === "/dashboard" &&
      userRole === "supplier_guest"
    ) {
      handleRedirect("/qr-dashboard");
      return;
    }

    // STRICT SEPARATION: Handle admin role redirects
    const businessRoutes = ['/dashboard', '/appointments', '/profile', '/stores', '/availability', '/billing'];
    const isBusinessRoute = businessRoutes.some(route => location.pathname.startsWith(route));
    
    if (userRole === "admin" && isBusinessRoute) {
      handleRedirect("/admin");
      return;
    }
    
    if (userRole === "admin_magasin" && location.pathname === "/dashboard") {
      handleRedirect("/magasin/admin/dashboard");
      return;
    }

    if (userRole === "admin_fournisseur" && location.pathname === "/dashboard") {
      handleRedirect("/fournisseur/admin/dashboard");
      return;
    }

    if (userRole === "admin_centrale" && location.pathname === "/dashboard") {
      handleRedirect("/centrale/dashboard");
      return;
    }

    // KAM: dedicated simplified dashboard, no access to /dashboard (commercial space)
    if (userRole === "kam" && location.pathname === "/dashboard") {
      handleRedirect("/kam/dashboard");
      return;
    }

    // Real GT chef_rayon on centrale store → redirect /dashboard to /centrale/rdv.
    // HQ support profiles and HQ users without GT mapping stay on the operational dashboard.
    if (userRole === "chef_rayon" && isCentraleGT && !isHqSupport && hasRealGTMapping && location.pathname === "/dashboard") {
      handleRedirect("/centrale/rdv");
      return;
    }

    // Check if user has required role
    if (!allowedRoles.includes(userRole)) {
      console.warn("[RouteGuard] Unauthorized route", {
        pathname: location.pathname,
        allowedRoles,
        userRole,
        normalizedRole: userRole,
        userId: user.id,
        profileRole: (userProfile as { role?: string | null } | null)?.role ?? null,
        profileStoreId: userProfile?.store_id ?? null,
        profileCentraleId: userProfile?.centrale_id ?? null,
        isPreviewing,
        realAuthUserId: realUserId,
        previewTargetUserId: preview?.targetUserId ?? null,
      });
      if (isPreviewing) {
        const targetRoute = dashboardForRole(userRole);
        if (targetRoute !== location.pathname) {
          console.info("[Preview][RouteGuard] redirect", {
            pathname: location.pathname,
            targetPreviewRole: userRole,
            realAuthUserId: realUserId,
            previewTargetUserId: preview?.targetUserId ?? null,
            targetRoute,
            reason: "route-not-allowed-for-preview-role",
          });
          handleRedirect(targetRoute);
          return;
        }
        setGuardStatus("unauthorized");
        return;
      }
      if (userRole === "admin") {
        handleRedirect("/admin");
      } else if (userRole === "admin_magasin") {
        handleRedirect("/magasin/admin/dashboard");
      } else if (userRole === "admin_fournisseur") {
        handleRedirect("/fournisseur/admin/dashboard");
      } else if (userRole === "admin_centrale") {
        handleRedirect("/centrale/dashboard");
      } else if (userRole === "kam") {
        handleRedirect("/kam/dashboard");
      } else if (userRole === "supplier_guest") {
        handleRedirect("/qr-dashboard");
      } else if (
        userRole === "fournisseur" ||
        userRole === "chef_rayon" ||
        userRole === "chef_secteur_magasin" ||
        userRole === "collaborateur_magasin"
      ) {
        handleRedirect("/dashboard");
      } else {
        setGuardStatus("unauthorized");
        return; // Show inline 403 page
      }
      return;
    }

    // All checks passed
    setGuardStatus("authorized");
  }, [isLoggingOut, isLoading, isGTLoading, isHqSupportLoading, isGTDeptLoading, isAuthenticated, user, userRole, userProfile, isCentraleGT, isHqSupport, hasRealGTMapping, allowedRoles, location.pathname, redirectTo, handleRedirect]);

  // Loading state
  if (guardStatus === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Profile missing — inline error page (no toast)
  if (guardStatus === "no_profile") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-6 text-center">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <h1 className="text-xl font-semibold text-foreground">Compte non rattaché</h1>
        <p className="text-muted-foreground max-w-md">
          Votre session est active mais votre compte n'est pas encore rattaché à une entreprise ou un magasin.
          Contactez votre SuperAdmin pour être associé à votre entité.
        </p>
        <div className="flex gap-3">
          <Button onClick={() => window.location.reload()} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Recharger
          </Button>
          <Button onClick={async () => { 
            const { supabase } = await import("@/integrations/supabase/client");
            await supabase.auth.signOut();
            navigate("/auth", { replace: true });
          }} variant="secondary">
            Se déconnecter
          </Button>
        </div>
      </div>
    );
  }

  // Unauthorized — inline 403 page (no toast)
  if (guardStatus === "unauthorized") {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 p-6 text-center">
        <h1 className="text-xl font-semibold text-foreground">Accès non autorisé</h1>
        <p className="text-muted-foreground max-w-md">
          Vous n'avez pas les permissions nécessaires pour accéder à cette page.
        </p>
        <Button onClick={() => navigate("/auth", { replace: true })} variant="outline">
          Retour à la connexion
        </Button>
      </div>
    );
  }

  // Redirecting (logged_out or role mismatch redirect)
  if (guardStatus !== "authorized") {
    return null;
  }

  return <>{children}</>;
}
